/**
 * Chat Store - Zustand state management for chat messages
 *
 * リアルタイムモード（useRealtimeChat フック使用）時:
 *   - メッセージは useRealtimeChat 経由で管理。chatStore の messages は使用しない
 *   - chatStore は入力状態管理（isInputEnabled）とバリデーションに特化
 *   - validateAndCheck でバリデーションのみ行い、送信は LecturePage で useRealtimeChat.sendMessage を呼ぶ
 *
 * モックモード（VITE_USE_MOCK_DATA=true）時:
 *   - 従来どおり chatStore.messages + localStorage でローカル管理
 *   - submitMessage でメッセージ追加 + localStorage 保存
 *
 * Validates: Requirements 5.1, 5.3, 5.4, 5.6, 5.8, 5.9, 5.10, 12.7
 */

import { create } from 'zustand';
import type { ChatMessage, ValidationResult } from '../types';
import { validateMessage, isChatClosed } from '../utils/chatValidator';
import {
  loadChatMessages,
  addChatMessage as addChatMessageToStorage,
} from '../utils/localStorage';

interface ChatStore {
  // State
  messages: ChatMessage[];
  inputValue: string;
  isInputEnabled: boolean;
  lectureId: string | null;

  // Actions
  setLectureId: (lectureId: string) => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  setInputValue: (value: string) => void;
  validateMessage: (
    message: string,
    userId: number,
    ngWords: string[],
    ngUsers: number[]
  ) => ValidationResult;
  /**
   * バリデーション + 入力可否チェックのみ行う（メッセージ追加しない）。
   * リアルタイムモードでの送信前チェックに使用。
   */
  validateAndCheck: (
    message: string,
    userId: number,
    ngWords: string[],
    ngUsers: number[]
  ) => ValidationResult;
  /**
   * モックモード用: バリデーション → ローカルにメッセージ追加 → 入力クリア
   */
  submitMessage: (
    message: string,
    userId: number,
    userName: string,
    ngWords: string[],
    ngUsers: number[]
  ) => ValidationResult;
  loadMessagesFromLocalStorage: (lectureId: string) => void;
  setInputEnabled: (enabled: boolean) => void;
  checkChatDeadline: (messageDeadline: string) => void;
  clearMessages: () => void;
  reset: () => void;
}

const initialState = {
  messages: [] as ChatMessage[],
  inputValue: '',
  isInputEnabled: true,
  lectureId: null as string | null,
};

export const useChatStore = create<ChatStore>((set, get) => ({
  ...initialState,

  setLectureId: (lectureId: string) => {
    set({ lectureId });
  },

  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, newMessage],
    }));

    const { lectureId } = get();
    if (lectureId) {
      addChatMessageToStorage(lectureId, newMessage);
    }
  },

  setInputValue: (value: string) => {
    set({ inputValue: value });
  },

  validateMessage: (
    message: string,
    userId: number,
    ngWords: string[],
    ngUsers: number[]
  ): ValidationResult => {
    return validateMessage(message, userId, ngWords, ngUsers);
  },

  validateAndCheck: (
    message: string,
    userId: number,
    ngWords: string[],
    ngUsers: number[]
  ): ValidationResult => {
    const { isInputEnabled } = get();
    if (!isInputEnabled) {
      return { isValid: false, error: 'closed' };
    }
    return validateMessage(message, userId, ngWords, ngUsers);
  },

  submitMessage: (
    message: string,
    userId: number,
    userName: string,
    ngWords: string[],
    ngUsers: number[]
  ): ValidationResult => {
    const { isInputEnabled } = get();
    if (!isInputEnabled) {
      return { isValid: false, error: 'closed' };
    }

    const validation = validateMessage(message, userId, ngWords, ngUsers);

    if (!validation.isValid) {
      return validation;
    }

    get().addMessage({
      userId: String(userId),
      userName,
      userType: 'student',
      message,
    });

    set({ inputValue: '' });

    return { isValid: true };
  },

  loadMessagesFromLocalStorage: (lectureId: string) => {
    const messages = loadChatMessages(lectureId);
    set({ messages, lectureId });
  },

  setInputEnabled: (enabled: boolean) => {
    set({ isInputEnabled: enabled });
  },

  checkChatDeadline: (messageDeadline: string) => {
    const closed = isChatClosed(messageDeadline);
    set({ isInputEnabled: !closed });
  },

  clearMessages: () => {
    set({ messages: [] });
  },

  reset: () => {
    set(initialState);
  },
}));
